import React, { useState } from 'react';
import { Button } from 'antd';
import { Auth } from 'aws-amplify';
import styled from '@emotion/styled';
import { Config } from 'vl-common/src/hooks/Runtime';
import NHSLoginButton from './NHSLoginButton';
import AXALoginButton from './AXALoginButton';
import LoginMethodTooltip from './LoginMethodTooltip';

const StyledMessage = styled.div`
  margin: 0 0 0.75em 0;
  text-align: center;
  font-size: 0.8rem;

  > .message {
    display: flex;
    flex-direction: column;

    > .hint {
      font-size: 14px;
      font-weight: 600;
      padding-bottom: 8px;
    }
  }
`;

const TOOLTIP_TOKEN = '{tooltip_token}';

const IdentityProviderItem = ({ message = '', children }: { message?: string; children: React.ReactNode }) => {
  const tokenIndex = message.indexOf(TOOLTIP_TOKEN) ?? -1;
  const hasTooltip = tokenIndex !== -1;
  const before = hasTooltip ? message.slice(0, tokenIndex) : message;
  const after = hasTooltip ? message.slice(tokenIndex + TOOLTIP_TOKEN.length) : undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {message && (
        <StyledMessage>
          {before && <span dangerouslySetInnerHTML={{ __html: before }} />}
          {hasTooltip && <LoginMethodTooltip />}
          {after && <span dangerouslySetInnerHTML={{ __html: after }} />}
        </StyledMessage>
      )}
      {children}
    </div>
  );
};

type IdentityProviders = Exclude<Config['IDENTITY_PROVIDERS'], null>['providers'][0];

/** SSO login button that abstracts away the specific nature of NHS Login */
const IdentityProviderButton = ({ identityProvider }: { identityProvider: Exclude<IdentityProviders, undefined> }) => {
  const { label, message = '', provider } = identityProvider;
  const [isLoading, setIsLoading] = useState(false);

  const onClick = async () => {
    if (provider === 'AXAPAT') setIsLoading(true);

    if (sessionStorage.getItem('restricted')) {
      sessionStorage.removeItem('restricted');
    }

    // @ts-ignore
    return Auth.federatedSignIn({ provider });
  };

  if (provider === 'NHSLogin') {
    return (
      <IdentityProviderItem key={provider} message={String(message)}>
        <NHSLoginButton data-testid="nhs-login-button" {...{ onClick }} />
      </IdentityProviderItem>
    );
  }

  if (provider === 'AXAPAT') {
    return (
      <IdentityProviderItem key={provider} message={String(message)}>
        <AXALoginButton
          data-testid="axa-login-button"
          {...{ onClick }}
          label={label || undefined}
          isLoading={isLoading}
        />
      </IdentityProviderItem>
    );
  }

  return (
    <IdentityProviderItem key={provider} message={String(message)}>
      <Button data-testid="sso" size="large" block type="primary" {...{ onClick }}>
        {label}
      </Button>
    </IdentityProviderItem>
  );
};

export default IdentityProviderButton;
